import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { IonContent, IonIcon, IonItem, IonLabel, IonList, IonListHeader, alertController, modalController, popoverController } from "@ionic/vue";
import { translate } from '@hotwax/dxp-components';
import { copyOutline, flashOutline, stopCircleOutline, timeOutline } from 'ionicons/icons'
import { computed } from "vue";
import JobHistoryModal from "@/components/JobHistoryModal.vue"
import { Plugins } from '@capacitor/core';
import { hasError, showToast } from "@/utils";
import logger from "@/logger";
import { ChannelService } from "@/services/ChannelService";
import { DateTime } from 'luxon';
import { useStore } from "vuex";


export default /*@__PURE__*/_defineComponent({
  __name: 'ShopActionsPopover',
  props: ["job"],
  setup(__props) {

const store = useStore();

const props = __props;
const currentEComStore = computed(() => store.getters["user/getCurrentEComStore"])

function closePopover() {
  popoverController.dismiss({ dismissed: true });
}

async function viewJobHistory() {
  const jobHistoryModal = await modalController.create({
    component: JobHistoryModal,
    componentProps: { currentJob: props.job }
  });

  jobHistoryModal.onDidDismiss().then(() => {
    closePopover();
  })

  await jobHistoryModal.present();
}

async function copyJobInformation() {
  const job = props.job;

  const { Clipboard } = Plugins;
  const jobDetails = `jobId: ${job.jobId}, jobName: ${job.enumName}, jobDescription: ${job.description} ${job.runtimeData ? (", runtimeData: " + JSON.stringify(job.runtimeData)) : ""}`;

  await Clipboard.write({
    string: jobDetails
  }).then(() => {
    showToast(translate("Copied to clipboard"));
  })

  closePopover();
}

async function disableJob() {
  const alert = await alertController.create({
    header: translate("Cancel job"),
    message: translate("Canceling this job will cancel this occurrence and all following occurrences. This job will have to be re-enabled manually to run it again."),
    buttons: [{
      text: translate("Don't cancel"),
      role: "cancel"
    }, {
      text: translate("Cancel"),
      handler: async() => {
        if(isRuntimePassed()) {
          showToast(translate("Job runtime has passed. Please refresh to get the latest job data in order to perform any action."))
          return;
        }

        try {
          const resp = await ChannelService.disableJob({ 
            jobId: props.job.jobId
          })

          if(!hasError(resp)) {
            showToast(translate("Successfully cancelled this job."))
            await store.dispatch("channel/fetchJobs");
            closePopover();
          } else {
            throw resp.data;
          }
        } catch(error: any) {
          showToast(translate("Failed to cancel this job."))
          logger.error(error);
        }
      }
    }],
  });

  return alert.present();
}

async function runNow() {
  const jobAlert = await alertController.create({
    header: translate("Run now"),
    message: translate("Running this job now will not replace this job. A copy of this job will be created and run immediately. You may not be able to reverse this action.", { space: '<br/><br/>' }),
    buttons: [
      {
        text: translate("Cancel"),
        role: 'cancel',
      },
      {
        text: translate("Run now"),
        handler: async() => {
          if(props.job) {
            runServiceNow(props.job);
          }
        }
      }
    ]
  });

  return jobAlert.present();
}

async function runServiceNow(job: any) {
  let resp;

  const payload = {
    'JOB_NAME': job.jobName,
    'SERVICE_NAME': job.serviceName,
    'SERVICE_COUNT': '0',
    'SERVICE_TEMP_EXPR': job.jobStatus,
    'jobFields': {
      'productStoreId': job.status === "SERVICE_PENDING" ? job.productStoreId : currentEComStore.value.productStoreId,
      'systemJobEnumId': job.systemJobEnumId,
      'tempExprId': job.jobStatus, // Need to remove this as we are passing frequency in SERVICE_TEMP_EXPR, currently kept it for backward compatibility
      'parentJobId': job.parentJobId,
      'recurrenceTimeZone': store.state.user.current.timeZone,
      'createdByUserLogin': store.state.user.current.username,
      'lastModifiedByUserLogin': store.state.user.current.username
    },
    'statusId': "SERVICE_PENDING",
    'systemJobEnumId': job.systemJobEnumId
  } as any

  Object.keys(job.runtimeData).map((key: any) => {
    if(key !== "productStoreId" && key !== "shopifyConfigId" && key !== "shopId") {
      payload[key] = job.runtimeData[key];
    }
  })

  // checking if the runtimeData has productStoreId, and if present then adding it on root level
  job?.runtimeData?.productStoreId?.length >= 0 && (payload['productStoreId'] = job.status === "SERVICE_PENDING" ? job.productStoreId : store.state.user.currentEComStore.productStoreId)
  job?.priority && (payload['SERVICE_PRIORITY'] = job.priority.toString())

  // ShopifyConfig and ShopifyShop should be set based upon runtime data
  // If existing job is run now, copy as is else set the current shop of user
  const jobRunTimeDataKeys = job?.runtimeData ? Object.keys(job?.runtimeData) : [];
  if (jobRunTimeDataKeys.includes('shopifyConfigId') || jobRunTimeDataKeys.includes('shopId')) {
    if (job.status !== "SERVICE_PENDING" && !job.shopifyConfigId) {
      showToast(translate('Shopify configuration not found. Scheduling failed.'))
      return;
    }

    jobRunTimeDataKeys.includes('shopifyConfigId') && (payload['shopifyConfigId'] = job.status === "SERVICE_PENDING" ? job.runtimeData?.shopifyConfigId : job.shopifyConfigId);
    jobRunTimeDataKeys.includes('shopId') && (payload['shopId'] = job.status === "SERVICE_PENDING" ? job.runtimeData?.shopId : job?.shopId);
    payload['jobFields']['shopId'] = job.status === "SERVICE_PENDING" ? job.shopId : job?.shopId;
  }

  try {
    resp = await ChannelService.scheduleJob({ ...payload });
    if(!hasError(resp)) {
      showToast(translate("Service has been scheduled."))
      closePopover();
    } else {
      throw resp.data;
    }
  } catch(err) {
    showToast(translate("Failed to schedule service."))
    logger.error(err)
  }
}

function isRuntimePassed() {
  return props.job.runTime <= DateTime.now().toMillis()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonList), null, {
        default: _withCtx(() => [
          _createVNode(_unref(IonListHeader), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(translate)("More options")), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            button: "",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (viewJobHistory()))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), {
                slot: "start",
                icon: _unref(timeOutline)
              }, null, 8, ["icon"]),
              _createVNode(_unref(IonLabel), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(translate)("History")), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            button: "",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (copyJobInformation()))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), {
                slot: "start",
                icon: _unref(copyOutline)
              }, null, 8, ["icon"]),
              _createVNode(_unref(IonLabel), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(translate)("Copy details")), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            button: "",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (runNow()))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), {
                slot: "start",
                icon: _unref(flashOutline)
              }, null, 8, ["icon"]),
              _createVNode(_unref(IonLabel), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(translate)("Run now")), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(IonItem), {
            button: "",
            lines: "none",
            disabled: __props.job.statusId === 'SERVICE_DRAFT',
            onClick: _cache[3] || (_cache[3] = ($event: any) => (disableJob()))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonIcon), {
                slot: "start",
                icon: _unref(stopCircleOutline),
                color: "danger"
              }, null, 8, ["icon"]),
              _createVNode(_unref(IonLabel), { color: "danger" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(translate)("Disable")), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})